import type { MRT_TableOptions } from 'mantine-react-table';

import { useTranslation } from 'react-i18next';

import { EmptyPlaceholder } from '../../../shared/components/UI/EmptyPlaceholder/EmptyPlaceholder';

import type { WorkspaceUser } from '../../../shared/entities/User';
import { normalizeString } from '../../../shared/utils/strings';
import { Column } from './useColumnsDefinition';
import { useFilters } from './useFilters.hook';

export function useTableConfig({
  onRowClick,
}: {
  onRowClick: (user: WorkspaceUser) => void;
}): Partial<MRT_TableOptions<WorkspaceUser>> {
  const { t } = useTranslation(['workspaceSettings', 'entities']);
  const filters = useFilters();

  return {
    ...BASE_TABLE_CONFIG,
    mantineTableBodyRowProps: ({ row: { original: user } }) => ({
      sx: { cursor: 'pointer' },
      onClick: () => {
        onRowClick(user);
      },
    }),
    enableGlobalFilter: true,
    state: {
      columnOrder: [],
      globalFilter: filters.search,
      columnFilters: [{ id: Column.ROLE, value: filters.roles }],
    },
    globalFilterFn: 'usersGlobalFilter',
    filterFns: {
      usersGlobalFilter: (row, _columnId, filterValue) => {
        const { firstName, lastName, email } = row.original as WorkspaceUser;
        const concatenatedValues = [firstName, lastName, email].join(' ');

        const normalizeFilterValue = normalizeString(filterValue);
        const normalizeConcatenatedValues = normalizeString(concatenatedValues);

        return normalizeConcatenatedValues.includes(normalizeFilterValue);
      },

      roleFilter: (row, _columnId, filterValue) => {
        if (filterValue.length === 0) {
          return true;
        }

        const { role } = row.original as WorkspaceUser;
        return filterValue.includes(role);
      },
    },
    renderEmptyRowsFallback: () => (
      <EmptyPlaceholder
        title={t('workspaceUserTable.emptyPlaceholder.title')}
        subtitle={t('workspaceUserTable.emptyPlaceholder.subtitle')}
      />
    ),
  };
}

const BASE_TABLE_CONFIG: Partial<MRT_TableOptions<WorkspaceUser>> = {
  memoMode: 'cells',
  enableSorting: false,
  enableColumnActions: false,
  enableTopToolbar: false,
  enableBottomToolbar: false,
  enablePagination: false,
  enableTableFooter: true,
  enablePinning: true,
  initialState: { density: 'xs' },
  mantinePaperProps: { withBorder: false },
  mantineTableProps: {
    sx: theme => ({
      '& tr > td, & tr > th': {
        borderBottom: `1px solid ${theme.colors.gray[1]} !important`,
      },
    }),
  },
  mantineTableHeadRowProps: { sx: () => ({ boxShadow: 'none' }) },
  mantineTableHeadCellProps: {
    sx: theme => ({
      padding: theme.spacing['03'] + '!important',
      div: {
        color: theme.colors.gray[6],
        fontWeight: 500,
        fontSize: theme.fontSizes.xs,
      },
    }),
  },
  mantineTableBodyProps: {
    sx: { 'tr:hover': { backgroundColor: 'white !important' } },
  },
  mantineTableBodyRowProps: {
    sx: { cursor: 'pointer' },
  },
  mantineTableBodyCellProps: { sx: { border: 'none !important' } },
  state: { columnOrder: [] },
};
